import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Upload,
  message,
  Space,
  Typography,
  Popconfirm,
  Empty,
  Spin,
  Image,
  Input,
  Row,
  Col,
  Tag,
  Alert,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  SaveOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { uploadAPI } from '../services/api';

const { Title, Text } = Typography;

interface MediaFile {
  filename: string;
  url: string;
  size: number;
  type: 'photo' | 'video';
  createdAt: string;
}

const MediaManagement: React.FC = () => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 配置相关
  const [uploadBaseUrl, setUploadBaseUrl] = useState('');
  const [savedBaseUrl, setSavedBaseUrl] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await uploadAPI.getAll();
      setFiles(response.data);
    } catch {
      message.error('获取文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await uploadAPI.getSettings();
      const val = response.data.uploadBaseUrl || '';
      setUploadBaseUrl(val);
      setSavedBaseUrl(val);
    } catch {
      // 表可能不存在，忽略
    }
  };

  useEffect(() => {
    fetchFiles();
    fetchSettings();
  }, []);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await uploadAPI.saveSettings(uploadBaseUrl.trim());
      setSavedBaseUrl(uploadBaseUrl.trim());
      message.success('配置已保存');
      // 刷新文件列表以更新 URL
      fetchFiles();
    } catch (err: any) {
      message.error(err.response?.data?.error || '保存失败');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file } = options;
    setUploading(true);
    try {
      await uploadAPI.upload(file as File);
      message.success('上传成功');
      fetchFiles();
    } catch (err: any) {
      message.error(err.response?.data?.error || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename: string) => {
    try {
      await uploadAPI.delete(filename);
      message.success('已删除');
      setFiles(files.filter(f => f.filename !== filename));
    } catch (err: any) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const copyUrl = (url: string) => {
    const fullUrl = url.startsWith('http') ? url : uploadAPI.getFullUrl(url);
    navigator.clipboard.writeText(fullUrl).then(() => {
      message.success('URL 已复制到剪贴板');
    }).catch(() => {
      const input = document.createElement('input');
      input.value = fullUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      message.success('URL 已复制到剪贴板');
    });
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('zh-CN');
  };

  const getPreviewUrl = (url: string) => {
    if (url.startsWith('http')) return url;
    return uploadAPI.getFullUrl(url);
  };

  const isConfigured = !!savedBaseUrl;
  const hasUnsavedChanges = uploadBaseUrl !== savedBaseUrl;

  return (
    <div>
      <Title level={3}>媒体文件管理</Title>

      {/* 公网 URL 配置 */}
      <Card
        size="small"
        title={
          <Space>
            <LinkOutlined />
            <span>公网访问配置</span>
            {isConfigured ? (
              <Tag icon={<CheckCircleOutlined />} color="success">已配置</Tag>
            ) : (
              <Tag icon={<WarningOutlined />} color="warning">未配置</Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        {!isConfigured && (
          <Alert
            message="Telegram 需要公网 URL 才能发送媒体文件"
            description="请填写你的服务器公网域名或 IP，并确保 nginx 已代理 /uploads/ 路径到后端。"
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="https://bot.yourdomain.com"
            value={uploadBaseUrl}
            onChange={e => setUploadBaseUrl(e.target.value)}
            addonBefore="基础URL"
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={savingSettings}
            onClick={handleSaveSettings}
            disabled={!hasUnsavedChanges}
          >
            保存
          </Button>
        </Space.Compact>
        {savedBaseUrl && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
            上传文件访问地址示例: {savedBaseUrl}/uploads/example.jpg
          </Text>
        )}
      </Card>

      <Divider />

      {/* 上传区域 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Upload
            accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
            showUploadList={false}
            customRequest={handleUpload}
            multiple
            disabled={uploading}
          >
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
              上传文件
            </Button>
          </Upload>
          <Button icon={<ReloadOutlined />} onClick={fetchFiles}>
            刷新
          </Button>
          <Text type="secondary">
            支持 JPG/PNG/GIF/WebP/MP4/MOV/WebM，图片最大 10MB，视频最大 50MB
          </Text>
        </Space>
      </div>

      {/* 文件列表 */}
      <Spin spinning={loading}>
        {files.length === 0 && !loading ? (
          <Empty description="暂无上传文件" />
        ) : (
          <Row gutter={[16, 16]}>
            {files.map((file) => {
              const previewUrl = getPreviewUrl(file.url);
              return (
                <Col key={file.filename} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    size="small"
                    hoverable
                    cover={
                      file.type === 'photo' ? (
                        <div style={{
                          height: 160,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafafa',
                        }}>
                          <Image
                            src={previewUrl}
                            alt={file.filename}
                            style={{ maxHeight: 160, objectFit: 'contain' }}
                            fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM5OTkiIGZvbnQtc2l6ZT0iMTIiPuWKoOi9veWksei0pTwvdGV4dD48L3N2Zz4="
                          />
                        </div>
                      ) : (
                        <div style={{
                          height: 160,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#fafafa',
                        }}>
                          <VideoCameraOutlined style={{ fontSize: 48, color: '#999' }} />
                        </div>
                      )
                    }
                    actions={[
                      <CopyOutlined key="copy" onClick={() => copyUrl(file.url)} title="复制URL" />,
                      <Popconfirm
                        key="delete"
                        title="确定删除此文件？"
                        onConfirm={() => handleDelete(file.filename)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <DeleteOutlined title="删除" />
                      </Popconfirm>,
                    ]}
                  >
                    <Card.Meta
                      title={
                        <Text ellipsis style={{ fontSize: 12 }} title={file.filename}>
                          {file.filename}
                        </Text>
                      }
                      description={
                        <Space size={4}>
                          <Tag color={file.type === 'photo' ? 'cyan' : 'purple'}>
                            {file.type === 'photo' ? <FileImageOutlined /> : <VideoCameraOutlined />}
                            {' '}{file.type === 'photo' ? '图片' : '视频'}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatSize(file.size)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {formatDate(file.createdAt)}
                          </Text>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default MediaManagement;
